import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useState } from 'react';

import { KpiCard } from '@/components/sales/KpiCard';
import { StatusChip } from '@/components/sales/StatusChip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useDebounce from '@/hooks/useDebounce';
import usePersistedState from '@/hooks/usePersistedState';
import {
  completeServiceTask,
  fetchServiceMetrics,
  fetchServiceTasks,
  patchServiceTask,
} from '@/services/service.service';
import type { ServiceTaskListItem, UpdateServiceTaskInput } from '@/services/service.service';
import cn from '@/utils/cn';
import {
  PAGINATION,
  QUERY_KEYS,
  type ServiceTaskSourceValue,
  type ServiceTaskStatusValue,
} from '@/utils/constants';

// ─── Progress bar ─────────────────────────────────────────────────────────────
interface ProgressBarProps {
  value: number;
  status: ServiceTaskStatusValue;
}

const ProgressBar = ({ value, status }: ProgressBarProps) => {
  const colorMap: Record<ServiceTaskStatusValue, string> = {
    'Not Picked Up': 'bg-muted-foreground/30',
    'In Progress': 'bg-primary',
    'On Hold': 'bg-warning',
    Blocked: 'bg-destructive',
    Completed: 'bg-success',
  };

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', colorMap[status])}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-[11px] tabular-nums text-muted-foreground">{value}%</span>
    </div>
  );
};

// ─── Source badge ─────────────────────────────────────────────────────────────
// Deactivation is called out in red: unlike every other task in this queue it
// asks for service to be switched OFF, and completing it must not be mistaken
// for a provisioning job (ADR-054).
const SOURCE_LABEL: Record<ServiceTaskSourceValue, string> = {
  billing: 'New',
  renewal: 'Renewal',
  deactivation: 'Deactivate',
  reactivation: 'Reactivate',
};

const SOURCE_CLASS: Record<ServiceTaskSourceValue, string> = {
  billing: 'bg-muted text-muted-foreground',
  renewal: 'bg-muted text-muted-foreground',
  deactivation: 'bg-danger-tint text-destructive',
  reactivation: 'bg-success-tint text-success',
};

const SourceBadge = ({
  source,
  cycleNumber,
}: {
  source: ServiceTaskSourceValue;
  cycleNumber: number | null;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
      SOURCE_CLASS[source],
    )}
  >
    {source === 'renewal' ? `Renewal C${cycleNumber ?? ''}` : SOURCE_LABEL[source]}
  </span>
);

// ─── Status update dropdown ───────────────────────────────────────────────────
type UpdatableStatus = Exclude<ServiceTaskStatusValue, 'Completed'>;

const UPDATABLE_STATUSES: UpdatableStatus[] = [
  'Not Picked Up',
  'In Progress',
  'On Hold',
  'Blocked',
];

interface StatusCellProps {
  task: ServiceTaskListItem;
  onStatusChange: (id: number, status: UpdatableStatus) => void;
  onComplete: (id: number) => void;
  loading: boolean;
}

const StatusCell = ({ task, onStatusChange, onComplete, loading }: StatusCellProps) => {
  if (task.status === 'Completed') {
    return <StatusChip tone="success">Completed</StatusChip>;
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select
        value={task.status}
        onChange={(e) => onStatusChange(task.id, e.target.value as UpdatableStatus)}
        disabled={loading}
        className="h-7 text-xs"
      >
        {UPDATABLE_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-[11px] text-success hover:border-success hover:text-success"
        onClick={() => onComplete(task.id)}
        disabled={loading}
        title="Mark as Completed"
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

// ─── Tab filters ─────────────────────────────────────────────────────────────
type TabKey = 'all' | 'active' | 'completed';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All Services' },
  { key: 'active', label: 'Active (In Progress · On Hold)' },
  { key: 'completed', label: 'Completed' },
];

const tabToStatus = (tab: TabKey): ServiceTaskStatusValue | undefined => {
  if (tab === 'completed') return 'Completed';
  return undefined;
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// ─── Page ─────────────────────────────────────────────────────────────────────
const ServicesPage = () => {
  const queryClient = useQueryClient();

  const [tab, setTab] = usePersistedState<TabKey>('service-tab', 'all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = usePersistedState('service-page', 1);
  const [limit, setLimit] = usePersistedState<number>('service-limit', PAGINATION.DEFAULT_LIMIT);
  const [mutatingId, setMutatingId] = useState<number | null>(null);

  const statusFilter = tabToStatus(tab);

  const queryParams = {
    search: debouncedSearch || undefined,
    status: statusFilter,
    page,
    limit,
  };

  const {
    data: tasksData,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useQuery({
    queryKey: [...QUERY_KEYS.SERVICE.LIST, queryParams],
    queryFn: () => fetchServiceTasks(queryParams),
  });

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: QUERY_KEYS.SERVICE.METRICS,
    queryFn: fetchServiceMetrics,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE.ROOT });
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateServiceTaskInput }) =>
      patchServiceTask(id, input),
    onMutate: ({ id }) => setMutatingId(id),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => completeServiceTask(id),
    onMutate: (id) => setMutatingId(id),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const handleStatusChange = (id: number, status: UpdatableStatus) => {
    updateMutation.mutate({ id, input: { status } });
  };

  const handleComplete = (id: number) => {
    completeMutation.mutate(id);
  };

  const handlePageChange = (next: number) => {
    const total = tasksData?.totalPages ?? 1;
    if (next >= 1 && next <= total) setPage(next);
  };

  const items = tasksData?.items ?? [];
  const totalPages = tasksData?.totalPages ?? 1;
  const totalCount = tasksData?.total ?? 0;

  // For "active" tab, filter client-side (In Progress + On Hold).
  const displayItems =
    tab === 'active'
      ? items.filter((t) => t.status === 'In Progress' || t.status === 'On Hold')
      : items;

  const fmtDate = (d: string | null) => {
    if (!d) return '-';
    const dt = new Date(`${d}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Services</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One row per product per client. Status: Not picked up → In Progress / On Hold → Completed.
          Marking Completed for a new client triggers Renewal queue entry.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Total Tasks (Per-Product)"
          value={metricsLoading ? '-' : (metrics?.total ?? 0)}
          tone="info"
        />
        <KpiCard
          label="Not Picked Up"
          value={metricsLoading ? '-' : (metrics?.notPickedUp ?? 0)}
          tone="warning"
        />
        <KpiCard
          label="In Progress"
          value={metricsLoading ? '-' : (metrics?.inProgress ?? 0)}
          tone="info"
        />
        <KpiCard
          label="On Hold"
          value={metricsLoading ? '-' : (metrics?.onHold ?? 0)}
          tone="warning"
        />
        <KpiCard
          label="Blocked"
          value={metricsLoading ? '-' : (metrics?.blocked ?? 0)}
          tone="destructive"
        />
        <KpiCard
          label="Completed (MTD)"
          value={metricsLoading ? '-' : (metrics?.completedMtd ?? 0)}
          tone="success"
          delta={metrics?.avgTatDays != null ? `${metrics.avgTatDays}d avg TAT` : undefined}
        />
      </div>

      {/* Tab filters */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={cn(
              'rounded-full border px-3.5 py-1 text-xs font-semibold transition-colors',
              tab === t.key
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + table */}
      <div className="rounded-lg border border-border bg-card shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
          <div className="relative flex-1 min-w-48">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by ARN or client..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
          </div>
          <Select
            value={String(limit)}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="w-24"
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </Select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Task</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="w-28">ARN</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="w-16">Source</TableHead>
                <TableHead className="w-48">Status</TableHead>
                <TableHead className="w-36">Progress</TableHead>
                <TableHead className="w-32">Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasksLoading && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    Loading tasks…
                  </TableCell>
                </TableRow>
              )}
              {tasksError && (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center text-sm text-destructive">
                    Failed to load tasks. Please try again.
                  </TableCell>
                </TableRow>
              )}
              {!tasksLoading && !tasksError && displayItems.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    No service tasks found.
                  </TableCell>
                </TableRow>
              )}
              {displayItems.map((task) => (
                <TableRow
                  key={task.id}
                  // A product marked for deactivation is tinted red across the
                  // whole row, so nobody works it as an ordinary activation.
                  className={cn(
                    'group',
                    task.source === 'deactivation' &&
                      task.status !== 'Completed' &&
                      'bg-danger-tint/60 hover:bg-danger-tint',
                  )}
                >
                  <TableCell className="font-mono text-xs font-bold text-foreground">
                    {task.taskCode}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-foreground">{task.clientName || '-'}</span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {task.arn ? `ARN-${task.arn}` : '-'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-sm font-semibold',
                      task.source === 'deactivation' ? 'text-destructive' : 'text-foreground',
                    )}
                  >
                    {task.productName}
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={task.source} cycleNumber={task.cycleNumber} />
                  </TableCell>
                  <TableCell>
                    <StatusCell
                      task={task}
                      onStatusChange={handleStatusChange}
                      onComplete={handleComplete}
                      loading={mutatingId === task.id}
                    />
                  </TableCell>
                  <TableCell>
                    <ProgressBar value={task.progress} status={task.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {fmtDate(task.dueDate)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-muted-foreground">
          <span>
            Showing {displayItems.length > 0 ? (page - 1) * limit + 1 : 0}–
            {Math.min(page * limit, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-14 text-center">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
